// The provenance line under every converted report.
//
// D6 / §07: FX is a DATED table, not one mutable setting, so that a report over
// a closed period never silently restates itself. That guarantee is worth
// nothing if the screen doesn't say which rate produced the figures — a reader
// has to be able to reproduce any number by hand from the rate and its
// `as_of`. So every report carries this line, INCLUDING the case where no
// conversion happened, because "no `fx`" and "converted at a rate I can't see"
// must not look the same.

import type { ReactNode } from "react";
import type { Currency, FxApplied, FxDisplay } from "@crm/shared";
import styles from "./FxFootnote.module.css";
import { formatCalendarDate, formatInstant } from "../format/calendarDate.js";
import { formatRate } from "../format/money.js";

export interface FxFootnoteProps {
  currency: Currency;
  fx: FxApplied | null;
  generatedAt: string;
}

export function FxFootnote({ currency, fx, generatedAt }: FxFootnoteProps): ReactNode {
  return (
    <div className={styles.footnote}>
      <span>
        {fx === null
          ? `All amounts in ${currency}. No FX conversion was needed — every source row was already in ${currency}.`
          : `All amounts in ${currency}, converted at USD→CAD ${formatRate(fx.usd_to_cad)} as of ${formatCalendarDate(fx.as_of)}.`}
      </span>
      <span className={styles.separator}>·</span>
      <span>Generated {formatInstant(generatedAt)}</span>
    </div>
  );
}

/**
 * The same line for the three reports that convert for DISPLAY ONLY — the bill
 * run, the calendar and the billing lineup.
 *
 * It says a DIFFERENT thing from the one above, which is why it is a different
 * component rather than a nullable prop. Those reports state every figure in
 * its own currency and their totals convert nothing, so this line is not
 * telling a reader how a figure was produced. It is telling them the opposite:
 * that no figure on the screen depends on a rate, and that the single
 * converted total the API carries beside them is a convenience taken at
 * today's rate rather than at the rate of any period on the screen.
 *
 * `unavailable` is worth a sentence of its own. It means the rate table holds
 * no row at all, which used to make these reports refuse outright; nothing on
 * the screen is missing because of it, and saying so stops a reader hunting
 * for a figure that was never going to be there.
 */
export interface FxDisplayFootnoteProps {
  fx: FxDisplay;
  generatedAt: string;
}

export function FxDisplayFootnote({ fx, generatedAt }: FxDisplayFootnoteProps): ReactNode {
  return (
    <div className={styles.footnote}>
      <span>{describeFxDisplay(fx)}</span>
      <span className={styles.separator}>·</span>
      <span>Generated {formatInstant(generatedAt)}</span>
    </div>
  );
}

const FX_DISPLAY_STATED = "Every figure is stated in its own currency, and no total mixes two.";

function describeFxDisplay(fx: FxDisplay): string {
  if (fx.status === "not_needed") {
    return `${FX_DISPLAY_STATED} Everything here is already in ${fx.target_currency}, so no FX rate was read.`;
  }
  // The rate and its date come as a pair with `display_converted`. The guard is
  // how that pairing is READ rather than asserted: a status without a rate
  // beside it describes the same screen as `unavailable` does, so it says so.
  const rate = fx.usd_to_cad;
  const asOf = fx.as_of;
  if (fx.status === "unavailable" || rate === null || asOf === null) {
    return `${FX_DISPLAY_STATED} No FX rate is on file, so no converted total is offered. Nothing above depends on one.`;
  }
  return `${FX_DISPLAY_STATED} The single converted total beside them uses USD→CAD ${formatRate(rate)} as of ${formatCalendarDate(asOf)} — the latest rate on file, read today, not the rate of any period shown.`;
}
